/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { Link } from 'react-router-dom'

import { formatRanAt } from './RunHistory'
import { STATUS_LABEL } from './SideBySide'
import type { RowStatus, RunResult, SideResult } from './types'

const SideLine: React.FC<{ name: string; side: SideResult }> = ({ name, side }) => (
  <div className="text-sm">
    <span className="font-semibold">{name}:</span>{' '}
    {side.ok ? (
      <>
        {side.count} rows{side.truncated ? ' (truncated)' : ''} in {side.ms} ms
      </>
    ) : (
      <span className="text-error">
        failed{side.status ? ` (${String(side.status)})` : ''}: {side.error}
      </span>
    )}
    {side.url && <span className="ml-2 font-mono text-xs text-base-content/60">{side.url}</span>}
  </div>
)

/** A one-line verdict: match / mismatch, with the row counts that explain it. */
export const MatchBadge: React.FC<{ result?: RunResult; running?: boolean }> = ({ result, running }) => {
  if (running) return <span className="loading loading-dots loading-xs" aria-label="running" />
  if (!result) return null
  if (!result.compare) return <span className="badge badge-error badge-sm">error</span>
  const c = result.compare
  return (
    <span
      className={`badge badge-sm ${c.match ? 'badge-success' : 'badge-error'}`}
      title={`Fresh ${String(c.freshCount)} / Timescale ${String(c.timescaleCount)}`}
    >
      {c.match ? 'match' : 'mismatch'} · {c.freshCount}/{c.timescaleCount}
    </span>
  )
}

interface Props {
  result: RunResult
  /** Link to the full side-by-side list. */
  compareTo?: string
}

export const RunSummary: React.FC<Props> = ({ result, compareTo }) => {
  const c = result.compare
  const counts = c ? (Object.entries(c.counts) as [RowStatus, number][]).filter(([, n]) => n > 0) : []
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <MatchBadge result={result} />
        {counts.map(([status, n]) => (
          <span key={status} className="badge badge-ghost badge-sm">
            {STATUS_LABEL[status]}: {n}
          </span>
        ))}
        {compareTo && c && (
          <Link to={compareTo} className="link link-primary text-sm" state={{ result }}>
            {c.match ? 'All rows' : 'All rows and discrepancies'} →
          </Link>
        )}
      </div>
      {result.run && <div className="text-xs text-base-content/60">Recorded {formatRanAt(result.run.ranAt)}</div>}
      <SideLine name="Fresh" side={result.fresh} />
      <SideLine name="Timescale" side={result.timescale} />
      {c && Object.keys(c.fieldMismatches).length > 0 && (
        <div className="text-sm">
          <span className="font-semibold">Fields that differ:</span>{' '}
          {Object.entries(c.fieldMismatches)
            .map(([f, n]) => `${f} (${String(n)})`)
            .join(', ')}
        </div>
      )}
      {c && Object.keys(c.knownGaps).length > 0 && (
        <div className="text-sm text-warning">
          <span className="font-semibold">Known gaps:</span>{' '}
          {Object.entries(c.knownGaps)
            .map(([f, g]) => `${f} — ${g.ticket} (${String(g.rows)})`)
            .join(', ')}
        </div>
      )}
    </div>
  )
}
